import classNames from 'classnames'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { SkeletonPlaceholders } from '@src/quince-components/src/atoms/skeletonPlaceholders'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { RECOMMENDATION_TRACKING_KEYS } from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/molecules/cartPopupTwo/molecules/recommendedForYou/molecules/product-list-carousel/product-list-carousel.constants'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import makeChunks from '@src/quince-site-common/src/utils/makeChunks'

import Swiper from '../../atoms/swiper'
import { DOTS_POSITION } from '../../atoms/swiper/Swiper'
import { TrackConfig } from '../../hocs/withTracking'

import InfiniteScrollProductList from './infiniteScrollProductList'
import styles from './productListSectionTwo.module.scss'

const PRODUCT_LIST_SECTION_TWO_ID = 'productListSectionTwo'

interface ProductListContentProps {
  productCards: IProductCard[]
  infiniteScroll?: boolean
  swiperClassName?: string
  gridColCount?: number
  tracker?: string
  id?: string
  productCardDesktopClassName?: string
  productCardExpClassName?: string
  productCardMobileClassName?: string
  wishlistIconOverrideClass?: string
  maxWidth?: number
  hideProductReviews?: boolean
  hideProductOptions?: boolean
  hideWishlistIcon?: boolean
  productClickTrackConfig?: TrackConfig
  hideProductName?: boolean
  productCardLinkClass?: string
  hideTags?: boolean
  skipTagRefresh?: boolean
  wishlistTrackConfig?: (productCard: IProductCard, idx: number) => Record<string, any>
  additionalProductTrackingContext?: (productCard: IProductCard, idx: number) => Record<string, any>
  productViewTracker?: string
  productPriceClass?: string
  isNewDesign?: boolean
  productCardImageSectionClass?: string
  trackingMetaData?: TrackingMetaData
  carouselArrowClickTracker?: ({ direction }: { direction: string }) => void
  productCardWrapperMobileClassName?: string
  chunkSize?: number
  prevIconClass?: string
  nextIconClass?: string
}

export function ProductListContent({
  productCards,
  infiniteScroll = false,
  swiperClassName,
  gridColCount,
  tracker,
  id,
  productCardDesktopClassName,
  productCardExpClassName,
  productCardMobileClassName,
  wishlistIconOverrideClass,
  maxWidth,
  hideProductReviews,
  hideProductOptions,
  hideWishlistIcon,
  productClickTrackConfig,
  hideProductName,
  productCardLinkClass,
  hideTags,
  skipTagRefresh,
  wishlistTrackConfig = () => ({}),
  additionalProductTrackingContext = () => ({}),
  productViewTracker,
  productPriceClass,
  isNewDesign,
  productCardImageSectionClass,
  trackingMetaData,
  carouselArrowClickTracker,
  productCardWrapperMobileClassName,
  chunkSize,
  prevIconClass,
  nextIconClass,
}: ProductListContentProps) {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const productCardChunks = makeChunks(productCards, chunkSize ?? 4)
  const _maxWidth =
    maxWidth ??
    getMaxWidthInPx({
      maxWidthMobileInRem: (isNewDesign ? styles.productCardWidthMobileNewDesign : styles.productCardWidthMobile) ?? '',
      maxWidthDesktopInRem: (isNewDesign ? styles.productCardWidthNewDesign : styles.productCardWidth) ?? '',
      deviceType: deviceInfo.deviceType,
    })

  const _showDots = isMobile
  const DESKTOP_VISIBLE_SLIDES = isNewDesign ? 6 : 4

  if (!productCards.length) {
    return <SkeletonPlaceholders length={5} />
  }

  if (infiniteScroll) {
    return (
      <InfiniteScrollProductList
        productCards={productCards}
        swiperClassName={swiperClassName}
        gridColCount={gridColCount}
        tracker={tracker}
        productCardDesktopClassName={productCardDesktopClassName}
        productCardExpClassName={productCardExpClassName}
        productCardMobileClassName={productCardMobileClassName}
        wishlistIconOverrideClass={wishlistIconOverrideClass}
        maxWidth={_maxWidth}
        hideReviews={hideProductReviews}
        hideOptions={hideProductOptions}
        hideWishlistIcon={hideWishlistIcon}
        productClickTrackConfig={productClickTrackConfig}
        optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
        hideProductName={hideProductName}
        productCardLinkClass={productCardLinkClass}
        hideTags={hideTags}
        skipTagRefresh={skipTagRefresh}
      />
    )
  }

  return (
    <Swiper
      className={classNames(styles.productList, swiperClassName)}
      numberOfVisibleSlides={isDesktop ? DESKTOP_VISIBLE_SLIDES : 1}
      showDots={_showDots}
      // Desktop: variableWidth={false} forces Swiper to calculate equal widths (container÷4), preventing the 5.28px gap
      // that occurs when container (132.5rem) exceeds content (4×33rem=132rem), which would show a ghost image of card 5
      variableWidth={isMobile}
      showNavigator={isDesktop}
      dotsPosition={DOTS_POSITION.BOTTOM}
      tracker={tracker}
      id={id ?? PRODUCT_LIST_SECTION_TWO_ID}
      arrowOnClick={carouselArrowClickTracker}
      trackingMetaData={trackingMetaData}
      prevIconClass={prevIconClass}
      nextIconClass={nextIconClass}
    >
      {isDesktop
        ? productCards.map((productCard, idx) => {
            const initialSelectedVariant = getInitialSelectedVariant({
              cardVariants: productCard.product.cardVariants,
            })
            const _tracker = tracker ? `${tracker}__0__${idx}` : undefined
            return (
              <ProductCard
                key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                className={classNames(
                  isNewDesign ? styles.productCardNewDesign : styles.productCard,
                  productCardDesktopClassName,
                  idx === productCards.length - 1 && styles.productCard__lastElement,
                )}
                productCard={productCard}
                maxWidth={_maxWidth}
                hideReviews={hideProductReviews}
                hideOptions={hideProductOptions}
                tracker={_tracker}
                productViewTracker={productViewTracker}
                wishlistTrackConfig={wishlistTrackConfig(productCard, idx)}
                skipTagRefresh={skipTagRefresh}
                hideWishlistIcon={hideWishlistIcon}
                productClickTrackConfig={
                  productClickTrackConfig
                    ? {
                        ...productClickTrackConfig,
                        context: {
                          ...productClickTrackConfig.context,
                          ...additionalProductTrackingContext(productCard, idx),
                          row: 0,
                          column: idx,
                          productPosition: idx,
                          contentContainerType: productClickTrackConfig.context?.contentContainerType,
                          contentContainerName: productClickTrackConfig.context?.contentContainerName,
                          contentContainerDisplayName: productClickTrackConfig.context?.contentContainerDisplayName,
                          tabData: {
                            ...(productClickTrackConfig.context?.tabData || {}),
                            rec_module_id: tracker ?? PRODUCT_LIST_SECTION_TWO_ID,
                            is_cart_modal: false,
                            product_position: { i: idx, j: 0 },
                          },
                        },
                      }
                    : undefined
                }
                shouldShowMore
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                productPriceClass={productPriceClass}
                onShowMoreClick={({ selectedVariant }) => {
                  navigateToPDP({
                    slug: productCard.product.slug,
                    tracker: _tracker,
                    selectedOption: selectedVariant?.displayConfig.value,
                    router,
                  })
                }}
                productCardLinkClass={productCardLinkClass}
                productCardImageSectionClass={productCardImageSectionClass}
                showMScroll={false}
                tabData={{
                  [RECOMMENDATION_TRACKING_KEYS.REC_ELEMENT_NAME]: String(
                    productClickTrackConfig?.context?.rec_element_name || PRODUCT_LIST_SECTION_TWO_ID,
                  ),
                  [RECOMMENDATION_TRACKING_KEYS.REC_PRODUCT_COUNT]: String(productCards.length),
                  [RECOMMENDATION_TRACKING_KEYS.IS_CART_MODAL]: false,
                  [RECOMMENDATION_TRACKING_KEYS.REC_MODULE_ID]: tracker ?? PRODUCT_LIST_SECTION_TWO_ID,
                  product_position: { i: idx, j: 0 },
                }}
              />
            )
          })
        : productCardChunks.map((productChunk, i) => (
            <div
              key={i}
              className={classNames(
                isNewDesign ? styles.productCardWrapperMobileNewDesign : styles.productCardWrapperMobile,
                productCardWrapperMobileClassName,
                i !== 0 && styles.productCardWrapperMobileChunks,
              )}
            >
              {productChunk.map((productCard, j) => {
                const initialSelectedVariant = getInitialSelectedVariant({
                  cardVariants: productCard.product.cardVariants,
                })
                const _tracker = tracker ? `${tracker}__${i}_${j}` : undefined
                const productIndex = productCardChunks.slice(0, i).reduce((acc, ch) => acc + ch.length, 0) + j
                const productPosition = { i: productIndex, j: 0 }
                return (
                  <ProductCard
                    key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                    className={classNames(
                      isNewDesign ? styles.productCardNewDesign : styles.productCard,
                      productCardMobileClassName,
                    )}
                    wishlistIconClassName={classNames(styles.productCard__wishlistIcon, wishlistIconOverrideClass)}
                    productCard={productCard}
                    maxWidth={_maxWidth}
                    hideReviews={hideProductReviews}
                    hideOptions={hideProductOptions}
                    tracker={_tracker}
                    productViewTracker={productViewTracker}
                    wishlistTrackConfig={wishlistTrackConfig(productCard, productCards.indexOf(productCard))}
                    skipTagRefresh={skipTagRefresh}
                    hideWishlistIcon={hideWishlistIcon}
                    productClickTrackConfig={
                      productClickTrackConfig
                        ? {
                            ...productClickTrackConfig,
                            context: {
                              ...productClickTrackConfig.context,
                              ...additionalProductTrackingContext(productCard, productCards.indexOf(productCard)),
                              row: i,
                              column: j,
                              contentContainerType: productClickTrackConfig.context?.contentContainerType,
                              contentContainerName: productClickTrackConfig.context?.contentContainerName,
                              contentContainerDisplayName: productClickTrackConfig.context?.contentContainerDisplayName,
                              tabData: {
                                ...(productClickTrackConfig.context?.tabData || {}),
                                rec_module_id: productCard.product.productId || 'PRODUCT_LIST_SECTION_TWO',
                                is_cart_modal: false,
                                product_position: productPosition,
                              },
                            },
                          }
                        : undefined
                    }
                    shouldShowMore
                    optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                    productPriceClass={productPriceClass}
                    onShowMoreClick={({ selectedVariant }) => {
                      navigateToPDP({
                        slug: productCard.product.slug,
                        tracker: _tracker,
                        selectedOption: selectedVariant?.displayConfig.value,
                        router,
                      })
                    }}
                    productCardLinkClass={productCardLinkClass}
                    hideTags={hideTags}
                    nameAndPriceSectionClass={isNewDesign ? styles.nameAndPriceSection : undefined}
                    productCardImageSectionClass={productCardImageSectionClass}
                    showMScroll={false}
                  />
                )
              })}
            </div>
          ))}
    </Swiper>
  )
}
